import type { HttpPost, HttpRequest } from '@/shared/infra/http'
import { HttpError, readServerErrorCode } from '@/shared/infra/http'
import type {
  InvitationGuestRequirements,
  InvitationIdentityConflict,
  InvitationOrganization,
  InvitationPreAuth,
  InvitationSessionTokens,
  InvitationTokenData,
  MembershipModel,
  VerifyInvitationToken,
  VerifyInvitationTokenOutcome,
  VerifyInvitationTokenParams,
} from '@/modules/auth/use-cases'

type VerifyRequestBody = {
  token: string
  preauth?: string
  createSession: boolean
  createForNewAccount?: boolean
}

type RawTokenData = {
  invite_id?: string
  invitation_id?: string
  invite_token?: string
  org_id?: string
  org_name?: string
  org_type?: 'club' | 'outfitter' | 'crew'
  role?: string
  type?: 'individual' | 'shared'
  email?: string | null
  phone?: string | null
  name?: string | null
  activationExperience?: 'member' | 'customer' | null
}

type RawOrganization = {
  id?: string
  name?: string
  logo_url?: string | null
  guest_requirements?: InvitationGuestRequirements
}

type RawSessionTokens = {
  access_token?: string | null
  refresh_token?: string | null
}

type RawPreAuth = Omit<InvitationPreAuth, 'registered' | 'phoneVerified'> & {
  registered?: boolean
  phoneVerified?: boolean
}

type RawIdentityConflict = {
  channel?: string | null
  maskedIdentifier?: string | null
}

type RawSuccessResponse = {
  success?: boolean
  alreadyClaimed?: boolean
  tokenData?: RawTokenData
  organization?: RawOrganization
  organizationName?: string
  preAuth?: RawPreAuth | null
  trustedEmail?: boolean
  session_tokens?: RawSessionTokens | null
  identityConflict?: RawIdentityConflict | null
  noAccount?: boolean
  membershipModel?: string | null
}

type RawErrorBody = {
  ok?: false
  error?: string
  organizationName?: string
  alreadyClaimed?: boolean
}

export class EdgeVerifyInvitationRepository {
  constructor(private readonly httpClient: HttpPost) {}

  execute: VerifyInvitationToken = async (
    params: VerifyInvitationTokenParams,
  ): Promise<VerifyInvitationTokenOutcome> => {
    try {
      const body: VerifyRequestBody = {
        token: params.token,
        preauth: params.preauth,
        createSession: params.createSession,
        createForNewAccount: params.createForNewAccount,
      }
      const request: HttpRequest<VerifyRequestBody> = {
        url: '/invitations/verify',
        body,
      }
      const response = await this.httpClient.post<VerifyRequestBody, RawSuccessResponse>(request)
      return mapSuccessResponseToOutcome(response)
    } catch (error) {
      return mapErrorToOutcome(error)
    }
  }
}

function mapSuccessResponseToOutcome(response: RawSuccessResponse): VerifyInvitationTokenOutcome {
  const raw = response ?? {}

  if (raw.alreadyClaimed) {
    const orgName = raw.tokenData?.org_name ?? raw.organizationName ?? ''
    const role = raw.tokenData?.role ?? ''
    const membershipModel = mapMembershipModel(raw.membershipModel)
    return {
      kind: 'already-claimed',
      orgName,
      role,
      ...(membershipModel ? { membershipModel } : {}),
    }
  }

  const tokenData = mapTokenData(raw.tokenData)
  const organization = mapOrganization(raw.organization)

  if (!tokenData || !organization) {
    return {
      kind: 'invalid',
      message: 'This invitation link is invalid.',
    }
  }

  const sessionTokens = mapSessionTokens(raw.session_tokens)
  const identityConflict = mapIdentityConflict(raw.identityConflict)

  return {
    kind: 'success',
    tokenData,
    organization,
    preAuth: mapPreAuth(raw.preAuth),
    trustedEmail: !!raw.trustedEmail,
    noAccount: !!raw.noAccount,
    ...(sessionTokens ? { sessionTokens } : {}),
    ...(identityConflict ? { identityConflict } : {}),
  }
}

function mapMembershipModel(raw?: string | null): MembershipModel | undefined {
  return raw === 'pro' || raw === 'seats' ? raw : undefined
}

function mapPreAuth(raw?: RawPreAuth | null): InvitationPreAuth | null {
  if (!raw) return null
  return { ...raw, registered: raw.registered === true, phoneVerified: raw.phoneVerified === true }
}

function mapIdentityConflict(
  raw?: RawIdentityConflict | null,
): InvitationIdentityConflict | undefined {
  if (!raw) return undefined
  const channel = raw.channel
  const maskedIdentifier = raw.maskedIdentifier
  if ((channel !== 'phone' && channel !== 'email') || !maskedIdentifier) {
    return undefined
  }
  return { channel, maskedIdentifier }
}

function mapTokenData(raw?: RawTokenData): InvitationTokenData | null {
  if (!raw) return null
  const orgId = raw.org_id
  const orgName = raw.org_name
  const inviteId = raw.invite_id ?? raw.invitation_id
  const inviteToken = raw.invite_token
  const role = raw.role
  if (!orgId || !orgName || !inviteId || !inviteToken || !role) return null
  return {
    orgId,
    orgName,
    inviteId,
    inviteToken,
    role,
    inviteType: raw.type,
    email: raw.email ?? undefined,
    phone: raw.phone ?? undefined,
    name: raw.name ?? undefined,
    orgType: raw.org_type,
    activationExperience: raw.activationExperience ?? undefined,
  }
}

function mapOrganization(raw?: RawOrganization): InvitationOrganization | null {
  if (!raw) return null
  const id = raw.id
  const name = raw.name
  const guestRequirements = raw.guest_requirements
  if (!id || !name || !guestRequirements) return null
  return {
    id,
    name,
    logoUrl: raw.logo_url ?? null,
    guestRequirements,
  }
}

function mapSessionTokens(raw?: RawSessionTokens | null): InvitationSessionTokens | undefined {
  if (!raw) return undefined
  const accessToken = raw.access_token
  const refreshToken = raw.refresh_token
  if (!accessToken || !refreshToken) return undefined
  return { accessToken, refreshToken }
}

const INVITATION_EXPIRED_CODE = 'INVITATION_EXPIRED'
const INVITATION_REVOKED_CODE = 'INVITATION_REVOKED'

function mapErrorToOutcome(error: unknown): VerifyInvitationTokenOutcome {
  if (error instanceof HttpError) {
    const body = (error.body ?? {}) as RawErrorBody
    const errorMessage = typeof body.error === 'string' ? body.error : ''
    const organizationName = body.organizationName
    const code = readServerErrorCode(error)

    if (code === INVITATION_EXPIRED_CODE) {
      return { kind: 'expired', organizationName, renewable: true }
    }

    if (code === INVITATION_REVOKED_CODE) {
      return { kind: 'expired', organizationName, renewable: false }
    }

    if (errorMessage.toLowerCase().includes('expired')) {
      return { kind: 'expired', organizationName, renewable: false }
    }

    if (
      errorMessage.toLowerCase().includes('already been claimed') ||
      errorMessage.toLowerCase().includes('already been used')
    ) {
      return { kind: 'already-registered', organizationName }
    }

    return {
      kind: 'invalid',
      message: errorMessage || 'This invitation link is invalid.',
    }
  }

  if (error instanceof Error) {
    const message = error.message ?? ''
    if (message.toLowerCase().includes('expired')) {
      return { kind: 'expired', renewable: false }
    }
    if (
      message.toLowerCase().includes('already been claimed') ||
      message.toLowerCase().includes('already been used')
    ) {
      return { kind: 'already-registered' }
    }
    return {
      kind: 'invalid',
      message: 'Unable to verify invitation. Please try again later.',
    }
  }

  return {
    kind: 'invalid',
    message: 'Unable to verify invitation. Please try again later.',
  }
}
